import { useEffect, useMemo } from "react"

import { Spin } from "antd"
import { useTranslation } from "react-i18next"
import { useSelector } from "react-redux"
import { useNavigate } from "react-router-dom"

import Button from "../../../Components/Button"
import EmptyComponent from "../../../Components/Empty"
import { RoutesPaths } from "../../../constants/user-routes"
import Icon from "../../../Icon"
import { ANALYTICS_SECTION } from "../redux/analytics.constants"
import { selectSectionData, selectSectionFetching } from "../redux/analytics.slice"
import { toWinRateGrid } from "../utils/breakdowns"
import Section from "./Section"
import WinRateGrid from "./WinRateGrid/WinRateGrid"

// "Win rate: category x network" heat map card. Rows = the 4 network categories plus
// Other, the same buckets the Disputes by category donut shows; columns = card schemes.
// Each cell is shaded by its win rate over that pair's resolved disputes (the grid itself
// is shared with the full inner page via WinRateGrid). The card shows only categories with
// something resolved; "View All" opens the full page with every category, including those
// still awaiting a first decision, and the exact figures behind each row.
const WinRateMatrix = ({ getData }) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const matrix = useSelector(selectSectionData(ANALYTICS_SECTION.WIN_RATE_MATRIX))
  const fetching = useSelector(selectSectionFetching(ANALYTICS_SECTION.WIN_RATE_MATRIX))

  useEffect(() => {
    getData(ANALYTICS_SECTION.WIN_RATE_MATRIX)
  }, [getData])

  const { cols, rows: allRows } = useMemo(() => toWinRateGrid(matrix), [matrix])
  // Only categories that actually have a value (at least one resolved, non-dash cell).
  // The slice is a backstop: the rollup yields five rows at most today, but it keeps the
  // card the same height as By card network if the category set ever grows.
  const rows = useMemo(
    () => allRows.filter((r) => r.cells.some((c) => c.winRate != null)).slice(0, 7),
    [allRows]
  )

  const title = t("Win rate: category × network")
  const tooltip = t(
    "Win rate for each category and card scheme, over the disputes we fought to a decision. Cases decided before you connected are excluded."
  )

  // Nothing decided yet: the card stays where it is, without "View All" (the inner page has
  // nothing to show either), and says why it is empty rather than going missing and leaving a
  // hole in the grid.
  if (!rows.length) {
    return (
      <Section title={title} tooltip={tooltip} minHeight={536}>
        <Spin spinning={fetching}>
          <EmptyComponent type="no_win_rate_yet" />
        </Spin>
      </Section>
    )
  }

  const viewAll = (
    <Button
      type="link"
      onClick={() => navigate(RoutesPaths.recover.analytics_win_rate)}
      icon={<Icon type="chevron_right_small" />}
      iconPosition="end"
      style={{ padding: 0 }}
      size="small"
      text={t("View All")}
    />
  )

  return (
    <Section title={title} tooltip={tooltip} extra={viewAll}>
      <Spin spinning={fetching}>
        <WinRateGrid cols={cols} rows={rows} />
      </Spin>
    </Section>
  )
}

export default WinRateMatrix
